/**
 * ============================================================================
 *  modeGuide.ts — Les trois états de l'interface guidée, et ce qui les relie
 * ----------------------------------------------------------------------------
 *  La machine à états est celle du moteur (`guided.state`) : on ne la double pas.
 *  Ce fichier ne fait que la LIRE avec les mots du design (« guide », « main »,
 *  « fin »), et porter ce que le moteur ne sait pas — l'exploration d'une scène
 *  depuis la fin de chapitre, la sortie vers la visite libre, le message d'accueil
 *  de la main. Fonctions pures, éprouvées sous `node`.
 *
 *    moteur       interface
 *    narration    guide        bandes noires, la caméra est au guide
 *    free         main         bandes rétractées, la voix continue
 *    chapterEnd   fin          écran de fin de chapitre
 *    chapterEnd   exploration  une scène du chapitre explorée SANS voix (« main » sans narration)
 * ==========================================================================*/

export type EtatMoteur = "idle" | "narration" | "chapterEnd" | "free";
export type ModeGuide = "guide" | "main" | "fin" | "exploration";

/** Le mode de l'interface, ou null hors parcours. */
export function modeGuide(etat: EtatMoteur, exploration: boolean): ModeGuide | null {
  switch (etat) {
    case "narration":
      return "guide";
    case "free":
      return "main";
    case "chapterEnd":
      return exploration ? "exploration" : "fin";
    default:
      return null;
  }
}

/** Les bandes noires sont à l'écran en guide et en fin ; la main (et l'exploration) les rétracte. */
export const bandesVisibles = (mode: ModeGuide): boolean => mode === "guide" || mode === "fin";

/** Le visiteur a la caméra : la barre flottante remplace les bandes. */
export const barreFlottante = (mode: ModeGuide): boolean => mode === "main" || mode === "exploration";

/* --- ce que le moteur ne porte pas ---------------------------------------- */

export type Annonce = "main" | "exploration" | null;

export interface EtatUi {
  /** Une scène du chapitre explorée depuis l'écran de fin. */
  exploration: boolean;
  /** Message affiché 3,5 s en haut de l'écran. */
  annonce: Annonce;
  /** Fondu vers le noir en cours : la sortie est lancée, la visite libre suit. */
  sortie: boolean;
  /** Fondu au noir entre deux chapitres : l'écran est noir le temps du changement de scène. */
  fondu: boolean;
}

export const etatUiInitial: EtatUi = { exploration: false, annonce: null, sortie: false, fondu: false };

export type EvenementUi =
  | { type: "mainPrise" }
  | { type: "explorer" }
  | { type: "retourChapitre" }
  | { type: "annonceExpiree" }
  | { type: "chapitreChange" }
  | { type: "fonduDebut" }
  | { type: "fonduFin" }
  | { type: "quitter" };

export function reduireUi(etat: EtatUi, evenement: EvenementUi): EtatUi {
  switch (evenement.type) {
    case "mainPrise":
      return { ...etat, annonce: "main" };
    case "explorer":
      return { ...etat, exploration: true, annonce: "exploration" };
    case "retourChapitre":
      return { ...etat, exploration: false, annonce: null };
    case "annonceExpiree":
      return { ...etat, annonce: null };
    case "chapitreChange":
      // Un autre chapitre : plus d'exploration ni de message du précédent.
      return { ...etat, exploration: false, annonce: null };
    case "fonduDebut":
      return { ...etat, fondu: true };
    case "fonduFin":
      return { ...etat, fondu: false };
    case "quitter":
      return { ...etat, sortie: true, annonce: null };
  }
}

/** Durée du fondu au noir entre deux chapitres (assombrir), en millisecondes. */
export const FONDU_CHAPITRE_MS = 500;
/** Au plus : si la scène du nouveau chapitre ne se montre pas, on rend l'image quand même. */
export const FONDU_ATTENTE_MAX_MS = 4000;

/** Durée du message d'accueil de la main, en millisecondes. */
export const DUREE_ANNONCE_MS = 3500;

/* --- clavier -------------------------------------------------------------- */

export type ActionClavier = "lecture" | "tourner-gauche" | "tourner-droite" | "reprendre" | "retour-chapitre" | null;

/**
 * Espace : lecture / pause. ← → : prendre la main et tourner. Échap : en main,
 * « Reprendre le guide » ; en exploration, « Retour au chapitre ».
 * Espace ne vaut pas sur un bouton ou un champ : c'est alors leur propre activation.
 */
export function actionClavier(mode: ModeGuide, touche: string, surControle: boolean): ActionClavier {
  if (touche === " " || touche === "Spacebar") {
    if (surControle || mode === "fin" || mode === "exploration") return null;
    return "lecture";
  }
  if (touche === "ArrowLeft" || touche === "ArrowRight") {
    if (mode === "fin") return null;
    return touche === "ArrowLeft" ? "tourner-gauche" : "tourner-droite";
  }
  if (touche === "Escape") {
    if (mode === "main") return "reprendre";
    if (mode === "exploration") return "retour-chapitre";
  }
  return null;
}

/** Le libellé du bouton de reprise : « Continuer » quand la scène explorée est finie. */
export const libelleReprise = (sceneFinie: boolean): "cont" | "resume" => (sceneFinie ? "cont" : "resume");
